import React from 'react';
import IndicatorTile from './IndicatorTile';
import CardHeader from './CardHeader';

interface OtherIndicatorsSectionProps {
  kommunId: string | null;
  skolaId: string | null;
  compareScope?: 'municipality' | 'national';
}

const OtherIndicatorsSection: React.FC<OtherIndicatorsSectionProps> = ({
  kommunId,
  skolaId,
  compareScope = 'municipality'
}) => {
  // Helper function to get indicator descriptions
  const getIndicatorDescription = (kpiId: string) => {
    switch (kpiId) {
      case 'N15809': // Elever i grundskola
        return 'Antal elever som är registrerade i grundskolan. Visar den totala elevpopulationen för den valda skolan eller kommunen. Källa: Skolverket (Siris).';
      case 'N15033': // Personaltäthet, elever
        return 'Antal elever per heltidsanställd lärare. Lägre tal betyder bättre personaltäthet och mer tid per elev. Källa: Skolverket (Siris).';
      case 'N15813': // Lärare med lärarlegitimation
        return 'Andel lärare som har lärarlegitimation. Procenttalet visar hur många av lärarna som har formell lärarutbildning och legitimation. Källa: Skolverket (Siris).';
      default:
        return 'Detta indikator visar information om skolans organisation och personal. Källa: Skolverket (Siris).';
    }
  };

  return (
    <section
      aria-labelledby="other-title"
      className="rounded-xl bg-slate-50/80 dark:bg-slate-800/50 ring-1 ring-slate-200 dark:ring-slate-700 p-4 md:p-5"
    >
      <header className="mb-3 md:mb-4">
        <CardHeader
          title="Övriga indikatorer"
          helpKind="indicator"
          shortText="Visar information om skolans organisation, personal och elevantal."
          longText="Dessa indikatorer visar information om skolans organisation, personal och elevantal. De uppdateras inte när du ändrar indikator-filter ovan. Källa: Skolverket (Siris)."
          helpTitle="Vad visar övriga indikatorer?"
        />
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs rounded-full bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200 px-2 py-0.5">
            {compareScope === 'municipality' ? 'Gäller: Kommun & Skola' : 'Gäller: Skola (jämför mot riket)'}
          </span>
          <p className="text-xs text-slate-600 dark:text-slate-300">
            Denna sektion <strong>ignorerar indikator-filtret</strong>.
          </p>
        </div>
      </header>

      <div className="grid gap-3 md:gap-4 lg:grid-cols-3">
        <IndicatorTile 
          title="Elever i grundskola" 
          kommunId={kommunId} 
          skolaId={skolaId}
          kpiId="N15809"
          unit=""
          helpText={getIndicatorDescription('N15809')}
          helpTitle="Vad visar antal elever?"
          compareScope={compareScope}
        />
        <IndicatorTile 
          title="Personaltäthet, elever" 
          kommunId={kommunId} 
          skolaId={skolaId}
          kpiId="N15033"
          unit=""
          helpText={getIndicatorDescription('N15033')}
          helpTitle="Vad betyder personaltäthet?"
          compareScope={compareScope}
        />
        <IndicatorTile 
          title="Lärare med lärarlegitimation, andel (%)" 
          kommunId={kommunId} 
          skolaId={skolaId}
          kpiId="N15813"
          unit="%"
          helpText={getIndicatorDescription('N15813')}
          helpTitle="Vad betyder lärarlegitimation?"
          compareScope={compareScope}
        />
      </div>
    </section>
  );
};

export default OtherIndicatorsSection;
